import type { ComponentType } from 'react';

import IconEnergy from 'assets/images/ai/icon-energy.svg';
import IconEveningTransition from 'assets/images/ai/icon-evening.svg';
import IconExperiencePrevious
  from 'assets/images/ai/icon-experience-previous.svg';
import IconMeeting from 'assets/images/ai/icon-meeting.svg';
import IconStartingMyDay from 'assets/images/ai/icon-starting-my-day.svg';
import IconWorkSession from 'assets/images/ai/icon-work-session.svg';

import type { VideoIntentGroupT } from '../intents';
import type { SessionKindT } from '../sessions';

type LifeMomentT = {
  /**
   * An optional, forced check out kind. If set for a life moment, all sessions
   * for that life moment will have this check out kind; otherwise, the check out
   * kind is decided by AI inside the prescription process.
   */
  checkOutKind?: SessionKindT;

  description: string;
  emotionalMechanisms: string[];
  icon: ComponentType;
  idealDurationMin: [number, number];
  idealEnvironments: string[];
  idealModalities: string[];

  /**
   * The direction of the state shift typical for this life moment, as a video
   * intent group: the check-in sends it as `intentGroup` of the experience
   * suggestion request when the user could not say how they want to feel, and
   * the engine then keeps every video within the group. Unset for the unknown
   * life moment.
   */
  intentGroup?: VideoIntentGroupT;

  name: string;
  neurologicalMechanisms: string[];
  physicalMechanisms: string[];
  shiftFrom: string[];
  shiftTo: string[];
};

export const LIFE_MOMENT_IDS = [
  '01_STARTING_THE_DAY',
  '02_PREPARING_FOR_SLEEP',
  '03_BEFORE_IMPORTANT_MEETING',
  '04_AFTER_WORK_TRANSITION',
  '05_MIDDAY_ENERGY_CRASH',
  '99_UNIQUE_UNKNOWN_LIFE_MOMENT',
] as const;

export type LifeMomentIdT = typeof LIFE_MOMENT_IDS[number];

export const LIFE_MOMENTS: Record<LifeMomentIdT, LifeMomentT> = {
  '01_STARTING_THE_DAY': {
    description: 'Beginning the day with a fresh mindset, easing into the morning, and getting ready for what lies ahead',
    emotionalMechanisms: [
      'Build optimism',
      'Create emotional predictability',
      'Establish intentionality',
      'Reduce anticipatory stress',
    ],
    icon: IconStartingMyDay,
    idealDurationMin: [5, 15],
    idealEnvironments: [
      'Bedroom',
      'Hotel room',
      'Living room',
      'Outdoors',
    ],
    idealModalities: [
      'Breath activation',
      'Dynamic mobility',
      'Intention meditation',
      'Light yoga flow',
      'Walking meditation',
    ],
    intentGroup: 'Energizing',
    name: 'Starting the day',
    neurologicalMechanisms: [
      'Circadian alignment',
      'Cortical activation',
      'Cortisol awakening response regulation',
      'Dopamine and norepinephrine stimulation',
    ],
    physicalMechanisms: [
      'Activate diaphragmatic breathing',
      'Increase circulation',
      'Increase oxygenation',
      'Mobilize spine and joints',
    ],
    shiftFrom: [
      'Foggy',
      'Disconnected',
      'Low-energy',
      'Reactive',
    ],
    shiftTo: [
      'Emotionally stable',
      'Energized',
      'Intentional',
      'Mentally clear',
    ],
  },
  '02_PREPARING_FOR_SLEEP': {
    checkOutKind: 'DELAYED_MOMENT_IMPACT',
    description: 'Winding down for the night, creating a calm atmosphere, and getting ready for restful sleep.',
    emotionalMechanisms: [
      'Emotional decompression',
      'Emotional settling',
      'Psychological safety',
      'Reduction of racing thoughts',
    ],
    icon: IconEveningTransition,
    idealDurationMin: [10, 30],
    idealEnvironments: [
      'Bedroom',
      'Darkened room',
      'Hotel room',
    ],
    idealModalities: [
      'Gentle stretching',
      'Meditation',
      'NSDR',
      'Slow breathwork',
      'Yoga nidra',
    ],
    intentGroup: 'Calming',
    name: 'Preparing for sleep',
    neurologicalMechanisms: [
      'Melatonin-supportive relaxation',
      'Parasympathetic activation',
      'Reduced cortical stimulation',
      'Reduced sympathetic tone',
    ],
    physicalMechanisms: [
      'Lower heart rate',
      'Slower breathing',
      'Muscle relaxation',
      'Reduced tension patterns',
    ],
    shiftFrom: [
      'Emotionally tense',
      'Mentally active',
      'Overstimulated',
    ],
    shiftTo: [
      'Deeply calm',
      'Safe',
      'Sleepy',
      'Slow',
    ],
  },
  '03_BEFORE_IMPORTANT_MEETING': {
    description: 'Taking a moment to prepare, focus, and gather confidence before an important meeting.',
    emotionalMechanisms: [
      'Confidence support',
      'Emotional grounding',
      'Reduced anticipatory anxiety',
      'Social readiness',
    ],
    icon: IconMeeting,
    idealDurationMin: [3, 10],
    idealEnvironments: [
      'Car',
      'Conference room',
      'Hallway',
      'Office',
    ],
    idealModalities: [
      'Breath regulation',
      'Confidence meditation',
      'Somatic grounding',
      'Visualization',
    ],
    intentGroup: 'Steadying',
    name: 'Before important meeting',
    neurologicalMechanisms: [
      'Attention stabilization',
      'Emotional regulation',
      'Executive function enhancement',
      'Reduction in amygdala overactivation',
    ],
    physicalMechanisms: [
      'Breath stabilization',
      'Postural alignment',
      'Reduced muscular tension',
      'Vocal relaxation',
    ],
    shiftFrom: [
      'Anxious',
      'Reactive',
      'Scattered',
    ],
    shiftTo: [
      'Articulate',
      'Confident',
      'Focused',
      'Grounded',
    ],
  },
  '04_AFTER_WORK_TRANSITION': {
    description: 'Shifting out of work mode, decompressing from the day, and making space to relax and recharge.',
    emotionalMechanisms: [
      'Emotional decompression',
      'Increased presence',
      'Separation from work identity',
    ],
    icon: IconWorkSession,
    idealDurationMin: [5, 20],
    idealEnvironments: ['Car', 'Home', 'Outdoors'],
    idealModalities: [
      'Breathwork',
      'Gentle movement',
      'Somatic shaking',
      'Walking reset',
    ],
    intentGroup: 'Calming',
    name: 'After work transition',
    neurologicalMechanisms: [
      'Attention shifting',
      'Reduced cognitive load',
      'Sympathetic downregulation',
    ],
    physicalMechanisms: [
      'Breath slowing',
      'Nervous system settling',
      'Shoulder/neck release',
    ],
    shiftFrom: ['Mentally overloaded', 'Work-oriented'],
    shiftTo: ['Present', 'Emotionally available', 'Relaxed'],
  },
  '05_MIDDAY_ENERGY_CRASH': {
    description: 'Experiencing a dip in energy and focus around midday, with a need to pause, reset, and recharge.',
    emotionalMechanisms: [
      'Mood uplift',
      'Motivation restoration',
      'Reduced overwhelm',
    ],
    icon: IconEnergy,
    idealDurationMin: [3, 10],
    idealEnvironments: ['Home', 'Office', 'Outdoors'],
    idealModalities: [
      'Energizing breathwork',
      'Dynamic movement',
      'Walking meditation',
      'Mobility flow',
    ],
    intentGroup: 'Energizing',
    name: 'Midday energy crash',
    neurologicalMechanisms: [
      'Attention restoration',
      'Dopaminergic activation',
      'Increased alertness',
    ],
    physicalMechanisms: [
      'Breath activation',
      'Increased circulation',
      'Postural activation',
    ],
    shiftFrom: ['Fatigue', 'Fogginess', 'Low motivation'],
    shiftTo: ['Alert', 'Mentally refreshed', 'Uplifted'],
  },
  '99_UNIQUE_UNKNOWN_LIFE_MOMENT': {
    description: 'Encountering an unexpected or hard-to-classify moment that does not fit the usual life moment categories.',
    emotionalMechanisms: [],
    icon: IconExperiencePrevious,
    idealDurationMin: [0, 60],
    idealEnvironments: [],
    idealModalities: [],
    name: 'Unique (unknown) life moment',
    neurologicalMechanisms: [],
    physicalMechanisms: [],
    shiftFrom: [],
    shiftTo: [],
  },
};
